import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, PlayCircle, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Save, Pencil, BookOpen, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function M365InboxPanel() {
  const [status, setStatus] = useState(null);
  const [probe, setProbe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfTesting, setPdfTesting] = useState(false);
  const [pdfTestResult, setPdfTestResult] = useState(null);
  const [polling, setPolling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ tenant_id: "", client_id: "", client_secret: "", mailbox: "", leads_mailbox: "", pdf_drive_user: "", poll_seconds: 300 });

  const load = async (populateForm = false) => {
    try {
      const r = await api.get("/admin/m365/status");
      setStatus(r.data);
      if (populateForm) {
        setForm({
          tenant_id: r.data?.config?.tenant_id || "",
          client_id: r.data?.config?.client_id || "",
          client_secret: "",
          mailbox: r.data?.config?.mailbox || "",
          leads_mailbox: r.data?.config?.leads_mailbox || "",
          pdf_drive_user: r.data?.config?.pdf_drive_user || "",
          poll_seconds: r.data?.config?.poll_seconds || 300,
        });
      }
    } catch (e) {
      toast.error("Could not load M365 status");
    }
  };

  useEffect(() => { load(true); }, []);

  // Re-populate the form when the admin explicitly enters edit mode
  useEffect(() => {
    if (editing) load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        tenant_id: form.tenant_id.trim(),
        client_id: form.client_id.trim(),
        mailbox: form.mailbox.trim(),
        leads_mailbox: (form.leads_mailbox || "").trim(),
        pdf_drive_user: (form.pdf_drive_user || "").trim(),
        poll_seconds: Number(form.poll_seconds) || 300,
      };
      // Only send the secret when the admin has typed a new value; empty means keep existing.
      if (form.client_secret && form.client_secret.trim()) body.client_secret = form.client_secret.trim();
      await api.put("/admin/m365/config", body);
      toast.success("Configuration saved — hot-swapped into the running poller");
      setEditing(false);
      setForm((f) => ({ ...f, client_secret: "" }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const test = async () => {
    setBusy(true);
    setProbe(null);
    try {
      const r = await api.post(`/admin/m365/test-connection?target=all`);
      setProbe(r.data);
      const results = r.data?.results || [];
      if (r.data?.ok) {
        toast.success(`All ${results.length} mailbox${results.length === 1 ? "" : "es"} reachable`);
      } else if (results.length) {
        const failed = results.filter((x) => !x.ok);
        toast.error(`${failed.length} of ${results.length} mailbox${results.length === 1 ? "" : "es"} failed`);
      } else {
        toast.error(r.data?.error || "Connection failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setBusy(false); }
  };

  const testPdfConversion = async () => {
    setPdfTesting(true);
    setPdfTestResult(null);
    try {
      const r = await api.post(`/admin/m365/test-pdf-conversion`);
      setPdfTestResult(r.data);
      if (r.data?.ok) {
        toast.success(`Microsoft Graph produced a ${Math.round((r.data.pdf_bytes || 0) / 1024)} KB PDF — advisories will use Graph.`);
      } else {
        toast.error(r.data?.error || "Graph PDF conversion failed — advisories will fall back to the native renderer.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF conversion test failed");
    } finally { setPdfTesting(false); }
  };

  const pollNow = async () => {
    setPolling(true);
    try {
      const r = await api.post("/admin/m365/poll-now");
      const { created = 0, comment = 0, leads_created = 0, leads_updated = 0, skipped = 0 } = r.data || {};
      const bits = [];
      if (created) bits.push(`${created} new ticket${created === 1 ? "" : "s"}`);
      if (comment) bits.push(`${comment} thread reply${comment === 1 ? "" : "ies"}`);
      if (leads_created) bits.push(`${leads_created} new lead${leads_created === 1 ? "" : "s"}`);
      if (leads_updated) bits.push(`${leads_updated} lead update${leads_updated === 1 ? "" : "s"}`);
      if (!bits.length && skipped) bits.push(`${skipped} skipped`);
      toast.success(`Polled — ${bits.join(", ") || "nothing new"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Poll failed");
    } finally { setPolling(false); }
  };

  const cfg = status?.config || {};
  const st = status?.state || {};
  const showForm = editing || !cfg.configured;

  return (
    <Card className="border-slate-200" data-testid="m365-inbox-panel">
      <CardHeader className="border-b border-slate-100 py-4 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <Mail className="h-4 w-4 text-blue-600" />
          Microsoft 365 — Support mailbox → Tickets
          <span className={`ml-2 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${
            cfg.configured ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : "bg-amber-50 text-amber-800 border border-amber-200"
          }`}>
            {cfg.configured ? "Configured" : "Not configured"}
          </span>
        </CardTitle>
        <div className="flex items-center gap-2">
          {cfg.configured && !editing && (
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid="m365-edit-btn">
              <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={test} disabled={!cfg.configured || busy} data-testid="m365-test-connection-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Test connection
          </Button>
          <Button size="sm" variant="outline" onClick={testPdfConversion} disabled={!cfg.configured || !cfg.pdf_drive_user || pdfTesting} data-testid="m365-test-pdf-btn" title={!cfg.pdf_drive_user ? "Set a PDF conversion user first" : "Attempt a real DOCX→PDF conversion via Microsoft Graph"}>
            <FileText className={`h-3.5 w-3.5 mr-1 ${pdfTesting ? "animate-pulse" : ""}`} /> Test PDF conversion
          </Button>
          <Button size="sm" onClick={pollNow} disabled={!cfg.configured || polling} data-testid="m365-poll-now-btn">
            <PlayCircle className={`h-3.5 w-3.5 mr-1 ${polling ? "animate-pulse" : ""}`} /> Poll now
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-5 space-y-4 text-sm">
        {showForm ? (
          <div className="space-y-4">
            {!cfg.configured && (
              <div className="rounded border border-amber-200 bg-amber-50/50 p-3 text-amber-900 text-xs flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  Paste the values from your Azure app registration below. In Azure Portal → your app → <b>Overview</b> for Tenant &amp; Client IDs, and <b>Certificates &amp; secrets</b> → <b>New client secret</b> for the Secret <b>Value</b> (not the Secret ID). Restart is NOT required — saving hot-swaps the running poller.
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormRow label="Tenant (directory) ID" testid="m365-form-tenant">
                <Input value={form.tenant_id} onChange={setField("tenant_id")} placeholder="00000000-0000-0000-0000-000000000000" data-testid="m365-input-tenant-id" />
              </FormRow>
              <FormRow label="Client (application) ID" testid="m365-form-client">
                <Input value={form.client_id} onChange={setField("client_id")} placeholder="00000000-0000-0000-0000-000000000000" data-testid="m365-input-client-id" />
              </FormRow>
              <FormRow label={cfg.has_secret ? "Client secret (leave blank to keep existing)" : "Client secret value"} testid="m365-form-secret">
                <Input type="password" value={form.client_secret} onChange={setField("client_secret")} placeholder={cfg.has_secret ? "•••••••• (unchanged)" : "The Value column from Azure"} data-testid="m365-input-client-secret" />
              </FormRow>
              <FormRow label="Support mailbox address" testid="m365-form-mailbox">
                <Input value={form.mailbox} onChange={setField("mailbox")} placeholder="support@xentra.co.uk" data-testid="m365-input-mailbox" />
              </FormRow>
              <FormRow label="Leads mailbox (optional — inbound form emails)" testid="m365-form-leads-mailbox">
                <Input value={form.leads_mailbox} onChange={setField("leads_mailbox")} placeholder="leads@xentra.co.uk" data-testid="m365-input-leads-mailbox" />
              </FormRow>
              <FormRow label="PDF conversion user — Microsoft Graph (required for Word-identical Threat Advisory PDFs; must be a licensed M365 account with a OneDrive)" testid="m365-form-pdf-drive-user">
                <Input value={form.pdf_drive_user} onChange={setField("pdf_drive_user")} placeholder="user@xentra.co.uk" data-testid="m365-input-pdf-drive-user" />
              </FormRow>
              <FormRow label="Poll interval (seconds)" testid="m365-form-poll">
                <Input type="number" min={60} max={3600} value={form.poll_seconds} onChange={setField("poll_seconds")} data-testid="m365-input-poll-seconds" />
              </FormRow>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              {cfg.configured && (
                <Button variant="outline" size="sm" onClick={() => { setEditing(false); load(); }} data-testid="m365-cancel-btn">Cancel</Button>
              )}
              <Button size="sm" onClick={save} disabled={saving || !form.tenant_id || !form.client_id || !form.mailbox || (!cfg.has_secret && !form.client_secret)} data-testid="m365-save-btn">
                <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving…" : "Save & apply"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Detail label="Support mailbox" value={cfg.mailbox} mono />
            <Detail label="Leads mailbox" value={cfg.leads_mailbox || "Not set"} mono />
            <Detail label="Graph PDF converter" value={cfg.pdf_drive_user || "Not set — advisories will fall back to the native PDF"} mono />
            <Detail label="Tenant" value={cfg.tenant_id} mono />
            <Detail label="Client ID" value={cfg.client_id} mono />
            <Detail label="Poll interval" value={`${cfg.poll_seconds}s`} mono />
          </div>
        )}

        {probe && (probe.results?.length ? (
          <div className="rounded border border-slate-200 p-3 space-y-1.5 bg-slate-50/40" data-testid="m365-probe-results">
            {probe.results.map((r) => (
              <div key={r.name} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded ${r.ok ? "bg-emerald-50 text-emerald-900" : "bg-red-50 text-red-900"}`} data-testid={`m365-probe-row-${r.name}`}>
                {r.ok ? <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" /> : <XCircle className="h-3.5 w-3.5 flex-shrink-0" />}
                <span className="font-mono uppercase tracking-wider text-[10px] px-1.5 py-0.5 rounded border border-current opacity-70 flex-shrink-0">{r.name}</span>
                <span className="font-mono">{r.mailbox || "not set"}</span>
                {r.ok ? (
                  <span className="text-emerald-700 text-[11px] ml-auto">Inbox {r.inbox?.total ?? "?"} · {r.inbox?.unread ?? "?"} unread</span>
                ) : (
                  <span className="text-red-700 text-[11px] ml-auto truncate">{r.error || "failed"}</span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className={`rounded border p-3 text-xs ${probe.ok ? "border-emerald-200 bg-emerald-50/50 text-emerald-900" : "border-red-200 bg-red-50/50 text-red-900"}`}>
            {probe.ok ? (
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Reached <b>{probe.displayName || probe.mail}</b> via Microsoft Graph</span>
            ) : (
              <span className="flex items-center gap-1.5"><XCircle className="h-4 w-4" /> {probe.error}</span>
            )}
          </div>
        ))}

        {pdfTestResult && (
          <div className={`rounded border p-3 text-xs ${pdfTestResult.ok ? "border-emerald-200 bg-emerald-50/50 text-emerald-900" : "border-red-200 bg-red-50/50 text-red-900"}`} data-testid="m365-pdf-test-result">
            {pdfTestResult.ok ? (
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div className="space-y-0.5">
                  <div><b>Microsoft Graph produced a {Math.round((pdfTestResult.pdf_bytes || 0) / 1024)} KB PDF</b> using drive user <span className="font-mono">{pdfTestResult.drive_user}</span>.</div>
                  <div className="text-emerald-700/90">Threat Advisory PDFs will render Word-identical via this path.</div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div className="space-y-0.5">
                  <div><b>Graph PDF conversion failed.</b> Advisories will fall back to the native (ReportLab) renderer, which does NOT match the DOCX exactly.</div>
                  <div className="font-mono text-[11px] break-all">{pdfTestResult.error}</div>
                  <div className="text-red-700/90 mt-1">
                    Most common fixes: (1) the drive user must be a <b>licensed</b> Microsoft 365 account with a OneDrive — shared mailboxes don't have one; (2) the Azure app registration needs <b>Files.ReadWrite.All</b> application permission with admin consent.
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="border-t border-slate-100 pt-4">
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">Poll state</div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-xs">
            <Stat label="Last run" value={st.last_run_finished ? new Date(st.last_run_finished).toLocaleString() : "—"} />
            <Stat label="Processed" value={st.processed_total || 0} />
            <Stat label="Tickets created" value={st.created_tickets || 0} accent="emerald" />
            <Stat label="Thread replies" value={st.appended_comments || 0} accent="blue" />
            <Stat label="Leads created" value={st.leads_created || 0} accent="emerald" />
            <Stat label="Leads updated" value={st.leads_updated || 0} accent="blue" />
          </div>
          {st.last_error && (
            <div className="mt-3 text-xs text-red-800 bg-red-50 border border-red-200 rounded p-2">
              <span className="font-mono text-[10px] uppercase tracking-wider">Last error {st.last_error_at ? `· ${new Date(st.last_error_at).toLocaleString()}` : ""}</span>
              <div className="mt-0.5">{st.last_error}</div>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 pt-4 text-xs text-slate-600 space-y-1">
          <p className="font-medium text-slate-800">How it works</p>
          <p className="text-slate-800 mt-1">Support mailbox → Tickets</p>
          <ul className="list-disc ml-5 space-y-0.5">
            <li>Every {cfg.poll_seconds || 300}s the backend fetches unread messages from <span className="font-mono">{cfg.mailbox || "the support mailbox"}</span>.</li>
            <li>Sender email is matched against your customer contacts; the ticket is auto-assigned to that account's owner.</li>
            <li>Subject → ticket subject, body → fault description, attachments → uploaded to object storage.</li>
            <li>Replies with <span className="font-mono">[TKT-1234]</span> in the subject append a comment to that existing ticket.</li>
            <li>Unknown senders create a ticket under the "Unmatched inbox (auto)" account with an <span className="font-mono">unknown-sender</span> tag.</li>
            <li>Auto-responders (mailer-daemon, postmaster, Auto-Submitted headers) are silently skipped.</li>
          </ul>
          {cfg.leads_mailbox && (
            <>
              <p className="text-slate-800 mt-2">Leads mailbox → HubSpot inbound queue</p>
              <ul className="list-disc ml-5 space-y-0.5">
                <li>Every {cfg.poll_seconds || 300}s the same loop also polls <span className="font-mono">{cfg.leads_mailbox}</span>.</li>
                <li>The email body is parsed for <b>First Name / Last Name / Organisation / Business Email / Contact Number / Message</b> (any variant labels accepted).</li>
                <li>A queued lead appears on the Leads page under the "HubSpot inbound queue" for triage.</li>
                <li>If a queued lead with the same email already exists, it's updated in place rather than duplicated.</li>
              </ul>
            </>
          )}
        </div>

        <details className="border-t border-slate-100 pt-4 text-xs text-slate-700 group" data-testid="m365-setup-guide">
          <summary className="cursor-pointer font-medium text-slate-900 flex items-center gap-1.5 select-none">
            <BookOpen className="h-3.5 w-3.5 text-blue-600" />
            <span>Setup guide — Azure app registration (first-time only)</span>
            <span className="ml-1 text-[10px] text-slate-400 group-open:hidden">click to expand</span>
          </summary>
          <div className="mt-3 space-y-4 pl-1">
            <SetupStep n={1} title="Register a new Azure app">
              <ol className="list-decimal ml-5 space-y-1">
                <li>Sign in at <a className="text-blue-700 underline" href="https://entra.microsoft.com" target="_blank" rel="noreferrer">entra.microsoft.com</a> as a Global Admin.</li>
                <li>Go to <b>Identity → Applications → App registrations → New registration</b>.</li>
                <li>Name it e.g. <span className="font-mono">Xentra Support Mail Poller</span>. Choose <b>Accounts in this organizational directory only (single tenant)</b>. Leave Redirect URI blank. Click <b>Register</b>.</li>
                <li>On the app's Overview page, copy <b>Directory (tenant) ID</b> and <b>Application (client) ID</b>. You'll paste these into the form above.</li>
              </ol>
            </SetupStep>

            <SetupStep n={2} title="Grant the Mail.Read application permission">
              <ol className="list-decimal ml-5 space-y-1">
                <li>In the app, click <b>API permissions → Add a permission → Microsoft Graph → Application permissions</b>.</li>
                <li>Search for <span className="font-mono">Mail.Read</span>, tick it, click <b>Add permissions</b>. <b>Do NOT</b> select the Delegated version.</li>
                <li>Click <b>Grant admin consent for &lt;your tenant&gt;</b> and confirm. The status column must show a green tick.</li>
              </ol>
              <p className="text-slate-500 mt-1">Propagation can take a couple of minutes — if Test connection returns 403 right away, wait 5 min and try again.</p>
            </SetupStep>

            <SetupStep n={3} title="Create a client secret">
              <ol className="list-decimal ml-5 space-y-1">
                <li>Click <b>Certificates &amp; secrets → Client secrets → New client secret</b>.</li>
                <li>Description: <span className="font-mono">xentra-support-poller</span>. Expires: 24 months (rotate it before then). Click <b>Add</b>.</li>
                <li>Copy the <b>Value</b> column <b>immediately</b>. Azure won't show it again — only the Secret ID stays visible.</li>
                <li>Paste the Value into the "Client secret" field above. It's never returned to the browser after saving.</li>
              </ol>
            </SetupStep>

            <SetupStep n={4} title="Fill in the form above &amp; save">
              <ul className="list-disc ml-5 space-y-1">
                <li><b>Tenant (directory) ID</b> — from step 1 (looks like <span className="font-mono">1234abcd-…-…-…-…</span>)</li>
                <li><b>Client (application) ID</b> — from step 1</li>
                <li><b>Client secret</b> — the <b>Value</b> from step 3</li>
                <li><b>Support mailbox</b> — <span className="font-mono">support@xentra.co.uk</span></li>
                <li><b>Poll interval</b> — leave at 300 (every 5 min) unless you need it faster</li>
              </ul>
              <p className="mt-1">Click <b>Save &amp; apply</b>. The badge above will flip to "Configured" instantly. Then click <b>Test connection</b> — a green tick with your mailbox display name means you're done.</p>
            </SetupStep>

            <SetupStep n={5} title="Restrict access to only this mailbox (optional but recommended)" tone="amber">
              <p>By default, an app with <span className="font-mono">Mail.Read</span> application permission can read <b>every</b> mailbox in your tenant. Lock it down to just <span className="font-mono">support@xentra.co.uk</span> with an Exchange Application Access Policy:</p>
              <ol className="list-decimal ml-5 space-y-1 mt-1">
                <li>Ask an Exchange administrator to create a mail-enabled security group containing only <span className="font-mono">support@xentra.co.uk</span> (e.g. <span className="font-mono">graph-support-mailboxes@xentra.co.uk</span>).</li>
                <li>In an Exchange Online PowerShell session, run:
                  <pre className="bg-slate-900 text-slate-100 rounded p-2 mt-1 overflow-x-auto text-[10px] leading-4">{`Connect-ExchangeOnline

New-ApplicationAccessPolicy \`
  -AppId '<CLIENT-ID-FROM-STEP-1>' \`
  -PolicyScopeGroupId 'graph-support-mailboxes@xentra.co.uk' \`
  -AccessRight RestrictAccess \`
  -Description 'Xentra CRM support poller — support mailbox only'

Test-ApplicationAccessPolicy \`
  -Identity 'support@xentra.co.uk' \`
  -AppId '<CLIENT-ID-FROM-STEP-1>'`}</pre>
                </li>
                <li>The <span className="font-mono">Test-ApplicationAccessPolicy</span> line should return <span className="font-mono">Granted</span>. Try any other mailbox and it should return <span className="font-mono">Denied</span>.</li>
              </ol>
              <p className="mt-1 text-slate-500">Policy propagation can take up to an hour. If Test connection starts returning 403 after enabling this, wait then retry.</p>
            </SetupStep>

            <SetupStep n={6} title="Troubleshooting">
              <ul className="list-disc ml-5 space-y-1">
                <li><b>401 / invalid_client</b> — Client secret Value is wrong. Copy the <b>Value</b> column again (not Secret ID), re-paste and Save.</li>
                <li><b>403 / accessDenied</b> — Admin consent not granted yet (step 2) or Application Access Policy is blocking this mailbox (step 5).</li>
                <li><b>404 / ResourceNotFound</b> — Typo in the mailbox address, or the mailbox is on-prem (Exchange Online only).</li>
                <li><b>Test connection times out</b> — Egress to <span className="font-mono">graph.microsoft.com</span> and <span className="font-mono">login.microsoftonline.com</span> must be allowed by any firewall/proxy.</li>
              </ul>
            </SetupStep>
          </div>
        </details>
      </CardContent>
    </Card>
  );
}

const SetupStep = ({ n, title, tone = "blue", children }) => {
  const tones = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
  };
  return (
    <div className="border-l-2 border-slate-200 pl-3">
      <div className="flex items-center gap-2 mb-1">
        <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full border text-[10px] font-bold ${tones[tone]}`}>{n}</span>
        <span className="font-medium text-slate-900 text-xs">{title}</span>
      </div>
      <div className="text-slate-700 text-[11px] leading-5 space-y-1">{children}</div>
    </div>
  );
};

const FormRow = ({ label, testid, children }) => (
  <div className="space-y-1" data-testid={testid}>
    <Label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</Label>
    {children}
  </div>
);

const Detail = ({ label, value, mono = false }) => (
  <div>
    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-0.5">{label}</div>
    <div className={`text-slate-800 ${mono ? "font-mono text-[11px]" : ""}`}>{value || "—"}</div>
  </div>
);

const Stat = ({ label, value, accent }) => {
  const colors = { emerald: "text-emerald-700", blue: "text-blue-700" };
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-0.5">{label}</div>
      <div className={`font-heading text-lg font-bold ${colors[accent] || "text-slate-900"}`}>{value}</div>
    </div>
  );
};
